import { useMemo } from 'react';
import { RfUiSelectSearch } from 'renfe-components';
import { useDispatch, useSelector } from 'react-redux';
import { setStationSelected, setDestinySelected } from 'store/app/actions';
import {
  makeSelectEstaciones,
  makeSelectPracticaSeleccionada,
} from 'store/practicas/selectors';
import { language } from 'utils/literals';
import { stateLiteral } from 'utils/statesLiterals';
import { createStructuredSelector } from 'reselect';
import {
  makeSelectIsStudentProfile,
  makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected,
} from 'store/usuarios/selectors';
import { parseEstaciones } from 'store/practicas/sagas';
import {
  makeSelectStationFinSelected,
  makeSelectStationIniSelected,
} from 'store/app/selectors';
import { Estacion, PracticaSeleccionada } from 'store/practicas/types';

const selectors = createStructuredSelector({
  estaciones: makeSelectEstaciones(),
  practicaSeleccionada: makeSelectPracticaSeleccionada(),
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  //
  stationSelected: makeSelectStationIniSelected(),
  destinySelected: makeSelectStationFinSelected(),
  isStudentProfile: makeSelectIsStudentProfile(),
} as any);
const StationsContainer = ({triggerResetFilters = false}) => {
  const dispatch = useDispatch();

  const {
    practicaSeleccionada,
    estaciones,
    stationSelected,
    destinySelected,
    isStudentProfile,
  } = useSelector<any, any>(selectors);

  const editMode: boolean = useMemo(
    () => !!practicaSeleccionada.CUERPO_FICHA,
    [practicaSeleccionada]
  );

  const calculatedStationSelectedOptions = useMemo(() => {
    const options = estaciones.map((e: Estacion) => ({
      label: e.COD_ESTACION + ' - ' + e.DESC_ESTACION,
      value: e,
      active:
        stationSelected?.value?.COD_ESTACION === e.COD_ESTACION
          ? 'true'
          : 'false',
    }));
    return JSON.stringify(options);
  }, [estaciones, stationSelected]);

  const calculatedStationSelectedLabel: string = useMemo(() => {
    const stationCod = stationSelected?.value?.COD_ESTACION ?? '';
    if (stationCod && estaciones) {
      const station = estaciones.find(
        (estacion) => estacion.COD_ESTACION === stationCod
      );
      if (station) {
        return station.COD_ESTACION + ' - ' + station.DESC_ESTACION;
      }
    }
    return '';
  }, [estaciones, stationSelected]);

  const calculatedDestinySelectedOptions = useMemo(() => {
    const options = estaciones.map((e: Estacion) => ({
      label: e.COD_ESTACION + ' - ' + e.DESC_ESTACION,
      value: e,
      active:
        destinySelected?.value?.COD_ESTACION === e.COD_ESTACION
          ? 'true'
          : 'false',
    }));
    return JSON.stringify(options);
  }, [estaciones, destinySelected]);

  const calculatedDestinySelectedLabel: string = useMemo(() => {
    const destinyCod = destinySelected?.value?.COD_ESTACION ?? '';
    if (destinyCod && estaciones) {
      const station = estaciones.find(
        (estacion) => estacion.COD_ESTACION === destinyCod
      );
      if (station) {
        return station.COD_ESTACION + ' - ' + station.DESC_ESTACION;
      }
    }
    return '';
  }, [estaciones, destinySelected]);

  // Condiciones de edición

  const canEditStationSelected: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  const canEditDestinySelected: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  // Eventos

  const handleChangeStationSelected = (e: CustomEvent) => {
    // Sólo escuchamos eventos con detail === '' y con detail.value
    if (e.detail === '') {
      dispatch(setStationSelected(null));
    } else if (e.detail?.value) {
      const detailData = e.detail.value;
      dispatch(
        setStationSelected(
          parseEstaciones(detailData.COD_ESTACION, detailData.DESC_ESTACION)
        )
      );
    }
  };

  const handleChangeDestinySelected = (e: CustomEvent) => {
    // Sólo escuchamos eventos con detail === '' y con detail.value
    if (e.detail === '') {
      dispatch(setDestinySelected(null));
    } else if (e.detail?.value) {
      const detailData = e.detail.value;
      dispatch(
        setDestinySelected(
          parseEstaciones(detailData.COD_ESTACION, detailData.DESC_ESTACION)
        )
      );
    }
  };

  return (
    <>
      {editMode ? (
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.estacionesLabel}
          </h2>
          <div className="rf-practice-detail__input-container">
            <RfUiSelectSearch
              options={calculatedStationSelectedOptions}
              onOptionSelected={handleChangeStationSelected}
              disabled={!canEditStationSelected}
              text-label={language.informacionPractica.origenLabel}
              placeholder={language.informacionPractica.origenPlaceholder}
              textError={language.validations.multiselectEmpty}
              defaultValue={calculatedStationSelectedLabel}
              reset={triggerResetFilters}
            ></RfUiSelectSearch>
          </div>
          <div className="rf-practice-detail__input-container">
            <RfUiSelectSearch
              disabled={!canEditDestinySelected}
              options={calculatedDestinySelectedOptions}
              onOptionSelected={handleChangeDestinySelected}
              text-label={language.informacionPractica.destinoLabel}
              textError={language.validations.multiselectEmpty}
              placeholder={language.informacionPractica.destinoPlaceholder}
              defaultValue={calculatedDestinySelectedLabel}
              reset={triggerResetFilters}
            />
          </div>
        </div>
      ) : (
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.estacionesLabel}
          </h2>
          <div className="rf-practice-create__input-container">
            <RfUiSelectSearch
              options={calculatedStationSelectedOptions}
              onOptionSelected={handleChangeStationSelected}
              text-label={language.informacionPractica.origenLabel}
              placeholder={language.informacionPractica.origenPlaceholder}
              textError={language.validations.multiselectEmpty}
              defaultValue={calculatedStationSelectedLabel}
              reset={triggerResetFilters}
            />
          </div>
          <div className="rf-practice-create__input-container">
            <RfUiSelectSearch
              options={calculatedDestinySelectedOptions}
              onOptionSelected={handleChangeDestinySelected}
              placeholder={language.informacionPractica.destinoPlaceholder}
              textError={language.validations.multiselectEmpty}
              text-label={language.informacionPractica.destinoLabel}
              defaultValue={calculatedDestinySelectedLabel}
              reset={triggerResetFilters}
            />
          </div>
        </div>
      )}
    </>
  );
};
export default StationsContainer;
